import React, { createContext, useState, useContext, useEffect } from 'react';
import { translationService, publicTranslationService } from '../services/api';
import { resolveReceiptLanguage } from '../utils/receiptLanguage';
import {
    readCachedTranslations,
    toTranslationMaps,
    writeCachedTranslations,
} from './translationCache';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState('en');
    const [isRTL, setIsRTL] = useState(false);
    const [dynamicTranslations, setDynamicTranslations] = useState({});
    const [translationsLoaded, setTranslationsLoaded] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const [translationDetails, setTranslationDetails] = useState({});
    const [availableLanguages, setAvailableLanguages] = useState(['en', 'ar']);
    const [publicLanguages] = useState(['en', 'ar']); // Fixed public languages
    const [receiptTranslationLanguage, setReceiptTranslationLanguage] = useState('');
    const [receiptTranslationEnabled, setReceiptTranslationEnabled] = useState(false);

    // Load saved language preference
    useEffect(() => {
        const savedLanguage = localStorage.getItem('language') || 'en';
        const savedAvailableLanguages = JSON.parse(localStorage.getItem('availableLanguages') || '["en"]');
        const savedReceiptLang = localStorage.getItem('receiptTranslationLanguage') || '';
        const savedReceiptEnabled = localStorage.getItem('receiptTranslationEnabled') === 'true';

        setLanguage(savedLanguage);
        setAvailableLanguages(savedAvailableLanguages);
        setReceiptTranslationLanguage(savedReceiptLang);
        setReceiptTranslationEnabled(savedReceiptEnabled);
        setIsRTL(savedLanguage === 'ar');

        // Apply RTL to document
        if (savedLanguage === 'ar') {
            document.documentElement.dir = 'rtl';
            document.documentElement.lang = 'ar';
        } else {
            document.documentElement.dir = 'ltr';
            document.documentElement.lang = 'en';
        }
    }, []);

    const loadDynamicTranslations = async () => {
        const token = localStorage.getItem('token');
        const isPublicPage = window.location.pathname === '/' ||
            window.location.pathname.startsWith('/our-services') ||
            window.location.pathname.startsWith('/how-it-works') ||
            window.location.pathname.startsWith('/faq') ||
            window.location.pathname.startsWith('/contact-us') ||
            window.location.pathname === '/login';

        try {
            let allTranslations;

            if (token && !isPublicPage) {
                // User is authenticated and on app pages - use tenant-specific translations
                setIsAuthenticated(true);
                console.log('Loading tenant-specific translations...');

                // Always try the API first, fall back to cache when it fails.
                //
                // Host-agnostic on purpose: the fallback used to be gated on
                // an Electron-only capability flag, so Capacitor — the actual
                // POS runtime — had none, and one failed call at startup
                // rendered every string on the till as its raw key until the
                // app was relaunched. See translationCache.js.
                try {
                    allTranslations = await translationService.getAllTranslations();
                    writeCachedTranslations(allTranslations);
                    console.log(`Loaded ${allTranslations.length} translations from API`);
                } catch (err) {
                    const cached = readCachedTranslations();
                    if (!cached) throw err;
                    allTranslations = cached;
                    console.log(`Loaded ${cached.length} translations from cache (offline fallback)`);
                }

                // Load tenant config to get available languages
                try {
                    const authType = localStorage.getItem('auth_type');

                    if (authType === 'company_finance') {
                        // Company finance users should have access to all languages since they manage multiple tenants
                        console.log('Company finance user - enabling all languages');
                        setAvailableLanguages(['en', 'ar']);
                        localStorage.setItem('availableLanguages', JSON.stringify(['en', 'ar']));
                    } else {
                        // Regular tenant users
                        const tenantConfig = JSON.parse(localStorage.getItem('tenantConfig') || '{}');
                        const languages = tenantConfig.languages || ['en'];
                        console.log('Loading languages from tenant config:', languages);
                        setAvailableLanguages(languages);
                        localStorage.setItem('availableLanguages', JSON.stringify(languages));
                    }

                    // Only allow receipt translation if multiple languages exist
                    const currentLanguages = authType === 'company_finance' ? ['en', 'ar'] : JSON.parse(localStorage.getItem('tenantConfig') || '{}').languages || ['en'];
                    // Derived through the shared resolver so this and the POS
                    // toggle cannot disagree about which language will print.
                    // Note the old inline version fell back to
                    // `currentLanguages[1]`, which picks the SECOND language of
                    // a tenant that has no English at all — resolveReceiptLanguage
                    // falls back to the first instead.
                    const resolved = resolveReceiptLanguage(
                        currentLanguages,
                        localStorage.getItem('receiptTranslationLanguage'),
                    );
                    if (resolved) {
                        setReceiptTranslationLanguage(resolved);
                        localStorage.setItem('receiptTranslationLanguage', resolved);
                    } else {
                        // Disable receipt translation for single language tenants
                        setReceiptTranslationEnabled(false);
                        setReceiptTranslationLanguage('');
                        localStorage.setItem('receiptTranslationEnabled', 'false');
                        localStorage.setItem('receiptTranslationLanguage', '');
                    }
                } catch (error) {
                    console.error('Failed to load tenant languages:', error);
                    setAvailableLanguages(['en']); // Fallback to English only
                }
            } else {
                // User is not authenticated OR on public pages - use public translations
                setIsAuthenticated(false);
                console.log('Loading public translations...');
                allTranslations = await publicTranslationService.getAllTranslations();

                // Use fixed public languages
                setAvailableLanguages(publicLanguages);
                localStorage.setItem('availableLanguages', JSON.stringify(publicLanguages));
            }

            const translationMap = {};
            const detailsMap = {};
            allTranslations.forEach(translation => {
                translationMap[translation.english] = translation.translated_english;
                if (translation.translation_details) {
                    detailsMap[translation.english] = translation.translation_details;
                }
            });

            setDynamicTranslations(translationMap);
            setTranslationDetails(detailsMap);
            setTranslationsLoaded(true);
            console.log(`Loaded ${allTranslations.length} dynamic translations`);

        } catch (error) {
            console.error('Failed to load dynamic translations:', error);
            // Fall back to cache rather than leaving translationsLoaded=false,
            // which would leave the retry interval hammering the API with
            // repeated 401s. Every host, not just Electron: native runs the
            // same interval, so the branch that most needed this protection
            // was previously the one without it.
            const cached = readCachedTranslations();
            if (cached) {
                const { translationMap, detailsMap } = toTranslationMaps(cached);
                setDynamicTranslations(translationMap);
                setTranslationDetails(detailsMap);
                setTranslationsLoaded(true);
                console.log('Loaded translations from cache after API failure');
                return;
            }
            // No cache — mark loaded with empty translations to stop the retry
            // loop. Raw keys for this boot, but a quiet backend rather than a
            // stampede from every till at once.
            setTranslationsLoaded(true);
        }
    };
    
    const resetTranslations = () => {
        setDynamicTranslations({});
        setTranslationDetails({});
        setTranslationsLoaded(false);
        setIsAuthenticated(false);
    };

    // Load translations on mount and whenever translationsLoaded resets to false
    useEffect(() => {
        if (!translationsLoaded) {
            loadDynamicTranslations();
        }
    }, [translationsLoaded]);

    const toggleLanguage = () => {
        console.log('toggleLanguage called');
        console.log('Current language:', language);
        console.log('Available languages:', availableLanguages);

        const currentIndex = availableLanguages.indexOf(language);
        console.log('Current index:', currentIndex);

        const nextIndex = (currentIndex + 1) % availableLanguages.length;
        console.log('Next index:', nextIndex);

        const newLanguage = availableLanguages[nextIndex];
        console.log('New language will be:', newLanguage);

        setLanguage(newLanguage);
        setIsRTL(newLanguage === 'ar');
        localStorage.setItem('language', newLanguage);

        if (newLanguage === 'ar') {
            document.documentElement.dir = 'rtl';
            document.documentElement.lang = 'ar';
        } else {
            document.documentElement.dir = 'ltr';
            document.documentElement.lang = newLanguage;
        }

        console.log('Language switched to:', newLanguage);
        console.log('RTL mode:', newLanguage === 'ar');
    };

    const changeLanguage = (newLang) => {
        if (!newLang || newLang === language) return;
        if (!['en', 'ar'].includes(newLang)) return;
        setLanguage(newLang);
        setIsRTL(newLang === 'ar');
        localStorage.setItem('language', newLang);
        if (newLang === 'ar') {
            document.documentElement.dir = 'rtl';
            document.documentElement.lang = 'ar';
        } else {
            document.documentElement.dir = 'ltr';
            document.documentElement.lang = newLang;
        }

        // Reload translations when language changes
        loadDynamicTranslations();
    };

    const setReceiptTranslationSettings = (enabled, language = '') => {
        setReceiptTranslationEnabled(enabled);
        setReceiptTranslationLanguage(language);
        localStorage.setItem('receiptTranslationEnabled', enabled.toString());
        localStorage.setItem('receiptTranslationLanguage', language);
    };
    const t = (key, params = {}, fallback = key) => {
        let translation = key; // default fallback

        // Try dynamic translations first
        if (translationsLoaded && dynamicTranslations[key]) {
            const dynamicTranslation = dynamicTranslations[key][language];
            if (dynamicTranslation) {
                translation = dynamicTranslation;
            }
        }

        // If no translation found and fallback is provided, use fallback
        if (translation === key && fallback !== key) {
            translation = fallback;
        }

        // Handle parameter interpolation
        if (params && typeof params === 'object') {
            Object.keys(params).forEach(paramKey => {
                const placeholder = `{${paramKey}}`;
                translation = translation.replace(new RegExp(placeholder, 'g'), params[paramKey]);
            });
        }

        return translation;
    };

    // Add this new function after the existing `t` function
    const createReceiptTranslator = (targetLanguage) => {
        return (key, params = {}, fallback = key) => {
            let translation = key; // default fallback

            // Try dynamic translations first
            if (translationsLoaded && dynamicTranslations[key]) {
                const dynamicTranslation = dynamicTranslations[key][targetLanguage];
                if (dynamicTranslation) {
                    translation = dynamicTranslation;
                }
            }

            // If no translation found and fallback is provided, use fallback
            if (translation === key && fallback !== key) {
                translation = fallback;
            }

            // Handle parameter interpolation
            if (params && typeof params === 'object') {
                Object.keys(params).forEach(paramKey => {
                    const placeholder = `{${paramKey}}`;
                    translation = translation.replace(new RegExp(placeholder, 'g'), params[paramKey]);
                });
            }

            return translation;
        };
    };
    return (
        <LanguageContext.Provider value={{
            language,
            isRTL,
            toggleLanguage,
            changeLanguage,
            t,
            dynamicTranslations,
            translationDetails,
            translationsLoaded,
            createReceiptTranslator,
            loadDynamicTranslations,
            resetTranslations,
            isAuthenticated,
            availableLanguages,
            receiptTranslationLanguage,
            receiptTranslationEnabled,
            setReceiptTranslationSettings,
        }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => useContext(LanguageContext);

// Export standalone functions for backward compatibility
export const translateDynamic = (value, t) => {
    if (!value || typeof value !== 'string') return value;

    const translationKey = value
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');

    const translated = t ? t(translationKey) : translationKey;
    return translated !== translationKey ? translated : value;
};

export const getTransliteration = (englishText, language, t) => {
    if (!englishText) return englishText;

    // Use the translation system if available
    if (t) {
        const translated = t(englishText);
        if (translated !== englishText) {
            return translated;
        }
    }

    // Fallback to original text
    return englishText;
};

/**
 * Resolves the display name for a product in the given language.
 * Returns null when no translation_mode is set — callers should fall back to t(product.name).
 *
 * Usage:
 *   const displayName = getProductName(product, language, translationDetails) ?? t(product.name);
 */
export const getProductName = (product, language, translationDetails) => {
    if (!product || !product.translation_mode) return null;
    if (language === 'en') return null;

    const details = translationDetails?.[product.name]?.[language];

    if (product.translation_mode === 'manual') {
        const manual = product.manual_translation;
        if (typeof manual === 'string') {
            try { return JSON.parse(manual)?.[language] || null; } catch { return null; }
        }
        return manual?.[language] || null;
    }
    if (product.translation_mode === 'translation') {
        return details?.translation || null;
    }
    if (product.translation_mode === 'transliteration') {
        return details?.transliteration || null;
    }
    return null;
};

export const getEntityTranslation = (entity, language, translationDetails) => {
    if (!entity || !entity.translation_mode) return null;
    if (language === 'en') return null;

    const details = translationDetails?.[entity.name]?.[language];

    if (entity.translation_mode === 'manual') {
        const manual = entity.manual_translation;
        if (typeof manual === 'string') {
            try { return JSON.parse(manual)?.[language] || null; } catch { return null; }
        }
        return manual?.[language] || null;
    }
    if (entity.translation_mode === 'translation') {
        return details?.translation || null;
    }
    if (entity.translation_mode === 'transliteration') {
        return details?.transliteration || null;
    }
    return null;
};

export const formatTemplate = (template, variables = {}, t = null) => {
    if (!template) return '';

    let result = template;
    Object.keys(variables).forEach(key => {
        const placeholder = `{${key}}`;
        let value = variables[key];

        // If it's a category name and we have translation function, translate it
        if (key === 'category' && typeof value === 'string' && t) {
            value = translateDynamic(value, t);
        }

        result = result.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
    });
    return result;
};